/**
 * What StarScribe's shell reads from gyro-svc (docs/CONTRACT-starscribe-build.md section 8): GET /v1/starscribe/me, a page
 * row of GET /v1/starscribe/pages, and a created page (POST /v1/starscribe/pages answers `{ page }`). Each answer is
 * checked here; anything malformed is left out rather than shown half-read.
 */
import { StarscribeRequestError, isStarscribePageId } from '../client.js';
export function parseStarscribeMe(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    return { enabled: value.enabled === true };
}
const time = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
const name = (value) => typeof value === 'string' && value.trim() ? value.trim().slice(0, 120) : undefined;
/** A row, or a created page's meta, or null when it has no valid id and title. */
export function parsePageRow(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const value = raw;
    const title = typeof value.title === 'string' ? value.title.trim().slice(0, 200) : '';
    if (!isStarscribePageId(value.pageId) || !title)
        return null;
    const role = value.role === 'edit' || value.role === 'view' ? value.role : 'owner';
    const sharedWith = Array.isArray(value.sharedWith) ? value.sharedWith.map(name).filter((entry) => Boolean(entry)).slice(0, 20) : undefined;
    const version = typeof value.version === 'number' && Number.isSafeInteger(value.version) && value.version > 0 ? value.version : undefined;
    return {
        pageId: value.pageId.toLowerCase(),
        title,
        updatedAtMs: time(value.updatedAtMs) ?? time(value.createdAtMs) ?? 0,
        role,
        shared: value.shared === true || Boolean(sharedWith?.length),
        ...(name(value.ownerName) ? { ownerName: name(value.ownerName) } : {}),
        ...(sharedWith?.length ? { sharedWith } : {}),
        ...(time(value.deletedAtMs) ? { deletedAtMs: time(value.deletedAtMs) } : {}),
        ...(time(value.purgeAtMs) ? { purgeAtMs: time(value.purgeAtMs) } : {}),
        ...(version ? { version } : {}),
    };
}
/** GET /v1/starscribe/pages: its valid rows, newest first. */
export function parsePageRows(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const rows = Array.isArray(value.pages) ? value.pages.map(parsePageRow).filter((row) => row !== null) : [];
    return rows.sort((a, b) => (b.deletedAtMs ?? b.updatedAtMs) - (a.deletedAtMs ?? a.updatedAtMs));
}
/** The path of a page, or of one of its routes ("append", "recover", "purge"). */
export function pagePath(pageId, route) {
    return `/v1/starscribe/pages/${pageId}${route ? `/${route}` : ''}`;
}
/** What the person reads when a request was refused: the service's own sentence, else `fallback`. */
export function refusalText(error, fallback) {
    return error instanceof StarscribeRequestError && error.message ? error.message : fallback;
}
/**
 * Whether a failed GET /v1/starscribe/me is the service's "no" (403: the person is not verified; 404: the switch is off
 * for them or this surface, or a service without StarScribe) rather than a request that never got an answer: a 401 (in
 * the desktop app, the agent's own "sign in" when it has no silent Microsoft 365 token and sends nothing), a 429, a 5xx
 * (the agent's 502 and 504 included) or a dropped connection.
 */
export function answeredOff(error) {
    return error instanceof StarscribeRequestError && (error.status === 403 || error.status === 404);
}
/** An "off" answer is asked again when Gyro next comes on screen, at most this often: a switch turned on reaches an open widget. */
export const STARSCRIBE_ME_OFF_RECHECK_MS = 10 * 60_000;
/**
 * How long after its last try a /me that got no answer is tried again while Gyro stays on screen: 15 s, doubling with
 * each failure in a row, at most 5 minutes.
 */
export function starscribeMeRetryMs(failures) {
    return Math.min(15_000 * 2 ** Math.max(0, failures - 1), 5 * 60_000);
}
/**
 * While Gyro is on screen, when to ask /me next (milliseconds from `nowMs`; 0 is now), or null for not again: "on" is
 * kept; "off" is asked again once it is 10 minutes old, when Gyro next comes on screen; a try that got no answer is
 * tried again after its backoff, sooner never.
 */
export function starscribeMeNextAsk(known, nowMs) {
    if (!known)
        return 0;
    if (known.enabled)
        return null;
    const since = nowMs - known.atMs;
    if (known.failures === 0)
        return since >= STARSCRIBE_ME_OFF_RECHECK_MS ? 0 : null;
    return Math.max(0, starscribeMeRetryMs(known.failures) - since);
}
const BLOCK_ID = /^[A-Za-z0-9_-]{1,64}$/;
const SHORTCUT_ID = /^[a-z0-9_]{1,40}$/;
/**
 * An ask a page makes, `{ question, starscribe: { pageId, selection?, shortcut? } }`, exactly those fields, within the ask
 * route's limits (a selection of 1 to 200 block ids and up to 8,000 characters): the desktop's ask from a page's own window
 * crosses windows, so it is checked here again; null for anything else.
 */
export function parseStarscribeAsk(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const value = raw;
    const question = typeof value.question === 'string' ? value.question.trim() : '';
    const page = value.starscribe && typeof value.starscribe === 'object' ? value.starscribe : null;
    if (!question || question.length > 8000 || !page || !isStarscribePageId(page.pageId))
        return null;
    const starscribe = { pageId: page.pageId.toLowerCase() };
    const selection = page.selection && typeof page.selection === 'object' ? page.selection : null;
    if (selection) {
        const blockIds = Array.isArray(selection.blockIds) ? selection.blockIds.filter((id) => typeof id === 'string' && BLOCK_ID.test(id)) : [];
        if (!blockIds.length || blockIds.length > 200 || typeof selection.text !== 'string' || selection.text.length > 8000)
            return null;
        starscribe.selection = { blockIds, text: selection.text };
    }
    if (page.shortcut !== undefined) {
        if (typeof page.shortcut !== 'string' || !SHORTCUT_ID.test(page.shortcut))
            return null;
        starscribe.shortcut = page.shortcut;
    }
    return { question, starscribe };
}
//# sourceMappingURL=pages.js.map